// UserActions — the person's ONE registry and its overlays (ADR-036 §7 — the
// four kinds; the exemplar is `pages/teams/TeamActions.tsx`).
//   • `userActs` is the PURE registry — rows with a kind, the gate (`users:manage`
//     — the one people key, ADR-050) and a `show` predicate over the status and
//     the closed-over facts (self · the sign-in state · the chat account's
//     state); the shape test (`components/patterns/actionGrammar.test.ts`) walks it.
//   • `useUserActions` resolves it for a person and returns the ACTS every
//     surface composes from — the peek's top row + door through `ContextPanel`,
//     the page's seat + door through `EntityHeader` — and the OVERLAYS: every
//     dialog and drawer, mounted ONCE by the caller beside its surface.
// The acts: Edit access — the PRIMARY (the person's one editor, ADR-050 §2: the
// page's seat, the peek's main button, nowhere else — the Access card carries no
// link; one home per act) · the OPERATIONS on the web credential (Grant web
// access / Send password reset / Resend invitation — ONE verb, three honest
// words by the sign-in state: a LOCAL account recovers the password it has, an
// INVITED or lapsed one gets a new link replacing the one out there, a
// credential-less one the first setup link — ADR-003 §Federated identity; an SSO
// account never holds a local password, ADR-054; Cancel invitation — the grant's
// symmetry, live or lapsed) and on the chat account (Send Telegram invite · Send
// a new invite · Cancel invite · Send test message · Disconnect Telegram — by
// the identity's state; the Telegram section keeps the facts, the copy fields
// and the help) · the LIFECYCLE (Disable · Reactivate · Remove from project —
// the danger act, fenced last).
// BEHAVIOUR (the guardrail flows the backend's safety codes map to) stays in
// the handlers, never in the registry:
//   • disable a multi-project user as a non-sysadmin → USER_IN_MULTIPLE_PROJECTS
//     steers to "remove from this project instead";
//   • remove = the LEAVE (ADR-081): the dialog shows what ends and asks, place by
//     place, what becomes of the chains the person holds (a successor, or none);
//     the user's SOLE membership (is_sole_membership) → "remove and disable"
//     rides the same dialog in ONE atomic call;
//   • disable edits no chain: the dialog WARNS with the impact and offers "Hand over…";
//   • self hides every lifecycle and credential act (the `!isSelf` predicate;
//     CANNOT_*_SELF is the backend backstop) — the primary and the chat
//     account's verbs stay (a person may connect their own chat);
//   • a test message RESOLVES with a result either way (a status toast, never a
//     red alert); a double-send inside the invite cooldown is not an error.
// Decisions ride ConfirmDialog. INDEPENDENT boolean dialog states (not one enum)
// keep the disable → blocked → remove transitions clean: a ConfirmDialog auto-
// closes itself on a resolved onConfirm, which would null a shared enum and shut
// the dialog we just opened.

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { DepartureChain, GrantWebAccessResponse, ProjectUserDetail, SignIn, UserStatus } from '@ems-app/schemas/users/responses';
import type { ExternalIdentityInvite, ExternalIdentityState } from '@ems-app/schemas/users/telegram';
import { resolveActs, type ActionItem, type EntityAct } from '../../components/patterns/entityRegistry.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { Button } from '../../components/ui/Button.js';
import { icons } from '../../components/ui/icons.js';
import { useToast } from '../../components/ui/Toast.js';
import { useCan } from '../../hooks/useCan.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { useMe } from '../../hooks/useMe.js';
import { useProject } from '../../hooks/useProject.js';
import { useDisableUser, useGrantWebAccess, useReactivateUser, useRevokeWebInvite } from '../../hooks/useUserMutations.js';
import { useDepartureImpact } from '../../hooks/useUsers.js';
import { useDisconnectIdentity, useIssueInvite, useRevokeInvite, useTestMessage } from '../../hooks/useExternalIdentity.js';
import { formatList } from '../../lib/format.js';
import { apiErrorCode, apiErrorDetails } from '../../lib/apiError.js';
import { EditAccessDrawer } from './EditAccessDrawer.js';
import { HandoverDialog } from './HandoverDialog.js';
import { InviteHandover } from './InviteHandover.js';
import { LeaveDialog } from './LeaveDialog.js';
import { useRefusalToast } from '../../components/patterns/forms/useQuickSave.js';

/** What each act does — the hook wires these to its overlays and mutations. */
export interface UserActHandlers {
  editAccess: () => void;
  grantWebAccess: () => void;
  revokeWebInvite: () => void;
  telegramInvite: () => void;
  telegramReissue: () => void;
  telegramRevoke: () => void;
  telegramTest: () => void;
  telegramDisconnect: () => void;
  disable: () => void;
  reactivate: () => void;
  remove: () => void;
}

/** The closed-over facts the rows read beside the status. */
export interface UserActFacts {
  /** The target is the acting user — the lifecycle and credential acts hide (the backend backstops CANNOT_*_SELF). */
  isSelf: boolean;
  /** Chip 1 — the web credential's state: 'sso' hides the grant (a local password can never exist, ADR-054); 'local'
   *  reads reset, 'invited' / 'invite_expired' resend, a credential-less state grant (the first setup link). */
  signIn: SignIn;
  /** The chat account's state — which chat verbs show. */
  chat: ExternalIdentityState;
}

/** The person's registry — pure: the rows, their kinds, gates and predicates. */
export function userActs(h: UserActHandlers, facts: UserActFacts): ActionItem<UserStatus>[] {
  const invited = facts.signIn === 'invited' || facts.signIn === 'invite_expired';
  const grantLabelKey = facts.signIn === 'local' ? 'actions.resetPassword' : invited ? 'actions.resendInvitation' : 'actions.grantWebAccess';
  const chatPending = facts.chat === 'pending' || facts.chat === 'invite_expired';
  return [
    // The primary — the one editor (role · scope): the page's seat, the peek's main button, and nowhere else.
    { id: 'edit-access', kind: 'primary', labelKey: 'access.edit', icon: icons.edit, run: { handler: h.editAccess }, gate: 'users:manage', order: 10 },
    // The web credential: a non-SSO account (active or locked) — a LOCAL user gets a reset link, a credential-less
    // user the INVITATION that mints their first password; a disabled account's link could not complete.
    { id: 'grant-web-access', kind: 'operation', labelKey: grantLabelKey, icon: icons.password, run: { handler: h.grantWebAccess }, gate: 'users:manage', order: 10, show: (ctx) => !facts.isSelf && facts.signIn !== 'sso' && (ctx.status === 'active' || ctx.status === 'locked') },
    // The grant's symmetry: cancel the OUTSTANDING invitation (live or lapsed — also how the visible expired state clears without re-sending).
    { id: 'revoke-web-invite', kind: 'operation', labelKey: 'actions.cancelInvitation', icon: icons.close, run: { handler: h.revokeWebInvite }, gate: 'users:manage', order: 20, show: () => !facts.isSelf && invited },
    // The chat account, by its state: invite · re-send (replaces the outstanding one) + cancel · test + disconnect.
    { id: 'telegram-invite', kind: 'operation', labelKey: 'telegram.actions.invite', icon: icons.telegram, run: { handler: h.telegramInvite }, gate: 'users:manage', order: 30, show: () => facts.chat === 'not_connected' },
    { id: 'telegram-reissue', kind: 'operation', labelKey: 'telegram.actions.reissue', icon: icons.telegram, run: { handler: h.telegramReissue }, gate: 'users:manage', order: 30, show: () => chatPending },
    { id: 'telegram-revoke', kind: 'operation', labelKey: 'telegram.actions.revoke', icon: icons.close, run: { handler: h.telegramRevoke }, gate: 'users:manage', order: 40, show: () => chatPending },
    { id: 'telegram-test', kind: 'operation', labelKey: 'telegram.actions.test', icon: icons.telegram, run: { handler: h.telegramTest }, gate: 'users:manage', order: 50, show: () => facts.chat === 'linked' },
    { id: 'telegram-disconnect', kind: 'operation', labelKey: 'telegram.actions.disconnect', icon: icons.signOut, run: { handler: h.telegramDisconnect }, gate: 'users:manage', order: 60, show: () => facts.chat === 'linked' },
    // The lifecycle — the door; Remove is the danger act, fenced last.
    { id: 'reactivate', kind: 'lifecycle', labelKey: 'actions.reactivate', icon: icons.restore, run: { handler: h.reactivate }, gate: 'users:manage', order: 10, show: (ctx) => !facts.isSelf && ctx.status === 'disabled' },
    { id: 'disable', kind: 'lifecycle', labelKey: 'actions.disable', icon: icons.statusBlocked, run: { handler: h.disable }, gate: 'users:manage', order: 10, show: (ctx) => !facts.isSelf && ctx.status !== 'disabled' },
    { id: 'remove', kind: 'lifecycle', labelKey: 'actions.remove', icon: icons.delete, run: { handler: h.remove }, gate: 'users:manage', order: 20, tone: 'danger', show: () => !facts.isSelf },
  ];
}

export interface UseUserActionsOptions {
  /** After a successful remove (the page navigates back to the list; the peek closes). */
  onRemoved?: () => void;
}

export interface UserActions {
  /** The resolved set — what the shells compose from. */
  acts: EntityAct[];
  /** Every dialog and drawer, mounted ONCE by the caller beside its surface. */
  overlays: ReactNode;
}

export function useUserActions(projectId: string, user: ProjectUserDetail, { onRemoved }: UseUserActionsOptions = {}): UserActions {
  const { t } = useTranslation('users');
  const { t: tErrors } = useTranslation('errors');
  const toast = useToast();
  const can = useCan(projectId);
  const entitlements = useEntitlements(projectId);
  const meId = useMe().data?.user.id ?? '';
  const project = useProject(projectId);
  const projectName = project.data?.display_name ?? project.data?.name ?? '';
  const disable = useDisableUser(projectId);
  const reactivate = useReactivateUser(projectId);
  const grantWebAccess = useGrantWebAccess(projectId);
  const revokeInvite = useRevokeWebInvite(projectId);
  const issue = useIssueInvite(projectId, user.user_id);
  const revokeChat = useRevokeInvite(projectId, user.user_id);
  const refused = useRefusalToast('user-telegram');
  const test = useTestMessage(projectId, user.user_id);
  const disconnect = useDisconnectIdentity(projectId, user.user_id);

  const [accessOpen, setAccessOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [orphanOpen, setOrphanOpen] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  // The disable warning's impact (ADR-081 §5): the chains the person holds, read while the dialog is open.
  const impact = useDepartureImpact(projectId, user.user_id, disableOpen || handoverOpen);
  // The show-once payload from a completed grant — held ONLY in local state (the
  // link can never be re-fetched; only its hash is stored server-side).
  const [granted, setGranted] = useState<GrantWebAccessResponse | null>(null);
  // The chat invite just minted — handed over on the same screen (the peek has no field for its link and code).
  const [chatInvite, setChatInvite] = useState<ExternalIdentityInvite | null>(null);
  const [blockedCount, setBlockedCount] = useState(0);

  const name = `${user.first_name} ${user.last_name}`;
  const isSelf = user.user_id === meId;
  const invited = user.sign_in === 'invited' || user.sign_in === 'invite_expired';
  const grantCopy = user.sign_in === 'local' ? 'resetPassword' : invited ? 'resendInvite' : 'grantWeb';

  // The disable warning's sentences, each counted on its own and said only when it counts something: leads N ·
  // backs up M · the places left with no one · the rule (ADR-081 §5).
  const impactWords = (chains: readonly DepartureChain[]): string => {
    const leads = chains.filter((c) => c.position === 'lead').length;
    const backs = chains.filter((c) => c.position === 'backup').length;
    const alone = chains.filter((c) => c.alone).length;
    return [
      leads > 0 ? t('disable.leads', { count: leads }) : null,
      backs > 0 ? t('disable.backs', { count: backs }) : null,
      alone > 0 ? t('disable.alone', { count: alone }) : null,
      t('disable.impactKeep'),
    ]
      .filter((s): s is string => s !== null)
      .join(' ');
  };

  // The chat account's direct acts — the invite hands its link and code over (the handover, as the web grant);
  // the others say the outcome in a toast. The invalidate refetches the detail, so what renders after is exactly
  // what a reload renders (the PENDING read carries the outstanding credentials on the page).
  const issueInvite = () =>
    issue.mutateAsync().then(
      (invite) => setChatInvite(invite),
      refused,
    );
  const revokeChatInvite = () =>
    revokeChat.mutateAsync().then(
      () => toast.success(t('telegram.toasts.revoked')),
      refused,
    );
  // The reachability badge is a cached belief; a send proves it, and the server corrects the stored flag from
  // the result — so a wrong badge fixes itself the moment it is checked.
  const sendTest = () =>
    test.mutateAsync().then(
      (r) => {
        if (r.delivered) toast.success(t('telegram.test.sent', { name }));
        else toast.warning(t('telegram.test.failed', { reason: t(`telegram.test.reasons.${r.reason}`) }));
      },
      refused,
    );

  const acts = resolveActs(
    userActs(
      {
        editAccess: () => setAccessOpen(true),
        grantWebAccess: () => setGrantOpen(true),
        revokeWebInvite: () => setRevokeOpen(true),
        telegramInvite: () => void issueInvite(),
        telegramReissue: () => void issueInvite(),
        telegramRevoke: () => void revokeChatInvite(),
        telegramTest: () => void sendTest(),
        telegramDisconnect: () => setDisconnectOpen(true),
        disable: () => setDisableOpen(true),
        reactivate: () => setReactivateOpen(true),
        remove: () => (user.is_sole_membership ? setOrphanOpen(true) : setRemoveOpen(true)),
      },
      { isSelf, signIn: user.sign_in, chat: user.external_identity.state },
    ),
    { can, entitlements, status: user.status },
    (key) => t(key),
  );

  const overlays = (
    <>
      {/* The person's ONE editor (ADR-050 §2) — kept mounted (it slides), keyed by the person: a peek→peek swap
          or the route moving to another person must remount it, else it silently saves the previous one (the
          stale-edit rule, §Forms); it re-seeds from the current read on every open. */}
      {can('users:manage') && <EditAccessDrawer key={user.user_id} projectId={projectId} user={user} canGrantAdmin={can('admin')} open={accessOpen} onClose={() => setAccessOpen(false)} />}

      {/* Disable — attempt; a non-sysadmin multi-project block steers to remove. */}
      <ConfirmDialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        tone="danger"
        title={t('disable.title', { name })}
        description={t('disable.body')}
        confirmLabel={t('disable.confirm')}
        onConfirm={async () => {
          try {
            await disable.mutateAsync(user.user_id);
          } catch (err) {
            if (apiErrorCode(err) === 'USER_IN_MULTIPLE_PROJECTS') {
              const projects = apiErrorDetails(err).project_count;
              setBlockedCount(typeof projects === 'number' ? projects : 0);
              setBlockedOpen(true); // independent state — disable closes, blocked opens
              return;
            }
            throw err;
          }
        }}
      >
        {/* Disabling edits no chain (ADR-081 §5): the warning says what the person answers for HERE — leads N,
            backs up M, the places left with nobody — and offers the handover; a sysadmin also sees the chains held
            in OTHER projects, the projects named, even when none are held here (a disable reaches every project). */}
        {impact.data && (impact.data.chains.length > 0 || impact.data.elsewhere.length > 0) && (
          <div className="flex flex-col gap-2">
            {impact.data.chains.length > 0 && <p className="text-sm">{impactWords(impact.data.chains)}</p>}
            {impact.data.elsewhere.length > 0 && (
              <p className="text-sm text-[var(--color-text-secondary)]">
                {t(impact.data.chains.length > 0 ? 'disable.impactElsewhere' : 'disable.impactElsewhereOnly', {
                  count: impact.data.elsewhere.reduce((n, e) => n + e.chains, 0),
                  projects: formatList(impact.data.elsewhere.map((e) => e.name)),
                })}
              </p>
            )}
            {impact.data.chains.length > 0 && (
              <div>
                <Button variant="secondary" onClick={() => { setDisableOpen(false); setHandoverOpen(true); }}>
                  {t('departure.handOver')}
                </Button>
              </div>
            )}
          </div>
        )}
      </ConfirmDialog>

      {/* The handover before the lock — the same body as the leave dialog's; saving returns to the disable question. */}
      <HandoverDialog
        projectId={projectId}
        user={{ user_id: user.user_id, name }}
        chains={impact.data?.chains ?? []}
        open={handoverOpen}
        onClose={() => setHandoverOpen(false)}
        onSaved={() => { setHandoverOpen(false); setDisableOpen(true); }}
      />

      {/* Multi-project block → steer to "remove from this project instead". */}
      <ConfirmDialog
        open={blockedOpen}
        onClose={() => setBlockedOpen(false)}
        title={t('disableBlocked.title', { name, count: blockedCount })}
        description={t('disableBlocked.body')}
        confirmLabel={t('disableBlocked.confirm')}
        onConfirm={() => {
          setBlockedOpen(false);
          setRemoveOpen(true);
        }}
      />

      {/* Grant web access / send reset — mints the setup link (the user sets
          their own password) and hands back the SHOW-ONCE payload; the
          InviteHandover below is the success surface. */}
      <ConfirmDialog
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        tone="default"
        title={t(`${grantCopy}.title`, { name })}
        description={t(`${grantCopy}.body`)}
        confirmLabel={t(`${grantCopy}.confirm`)}
        onConfirm={async () => {
          try {
            setGranted(await grantWebAccess.mutateAsync(user.user_id));
          } catch (err) {
            // A double-send inside the cooldown is NOT an error — the outstanding
            // link stands. A warning toast (the dialog closes), never the red alert.
            if (apiErrorCode(err) === 'INVITE_JUST_SENT') {
              toast.warning(tErrors('INVITE_JUST_SENT'));
              return;
            }
            throw err;
          }
        }}
      />

      {/* Cancel the outstanding web invitation — the sent link stops working;
          the chip clears back to none. Re-sending later mints a fresh one. */}
      <ConfirmDialog
        open={revokeOpen}
        onClose={() => setRevokeOpen(false)}
        tone="danger"
        title={t('cancelInvite.title', { name })}
        description={t('cancelInvite.body')}
        confirmLabel={t('cancelInvite.confirm')}
        onConfirm={async () => {
          await revokeInvite.mutateAsync(user.user_id);
          toast.success(t('cancelInvite.done', { name }));
        }}
      />

      {/* The handover — the SAME screen the create wizard ends on (one language
          for one moment): green ✓ rows for the couriers that ran, the copyable
          link with its purpose in calm text, no alarm for a success. The raw
          link exists only in this response. */}
      <InviteHandover
        open={granted !== null}
        title={granted?.kind === 'reset' ? t('handover.titleReset', { name }) : t('handover.titleInvite', { name })}
        data={
          granted
            ? {
                name,
                email: user.email ?? null,
                web: {
                  link: granted.link,
                  expires_at: granted.expires_at,
                  email_sent: granted.email_sent,
                  telegram_sent: granted.telegram_sent,
                },
                chat: null,
                chatFailed: false,
                webFailed: false,
              }
            : null
        }
        onDone={() => setGranted(null)}
      />

      {/* The chat invite's handover — the same screen, its Telegram part alone. */}
      <InviteHandover
        open={chatInvite !== null}
        title={t('handover.titleInvite', { name })}
        data={chatInvite ? { name, email: user.email ?? null, web: null, chat: chatInvite, chatFailed: false, webFailed: false } : null}
        onDone={() => setChatInvite(null)}
      />

      {/* Disconnect the chat account — they stop receiving from every bot on the platform; a new invite reconnects. */}
      <ConfirmDialog
        open={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        tone="danger"
        title={t('telegram.disconnect.title')}
        description={t('telegram.disconnect.description', { name })}
        confirmLabel={t('telegram.disconnect.confirm')}
        onConfirm={async () => {
          await disconnect.mutateAsync();
          toast.success(t('telegram.toasts.disconnected'));
        }}
      />

      {/* Reactivate. */}
      <ConfirmDialog
        open={reactivateOpen}
        onClose={() => setReactivateOpen(false)}
        tone="default"
        title={t('reactivate.title', { name })}
        description={t('reactivate.body')}
        confirmLabel={t('reactivate.confirm')}
        onConfirm={async () => {
          await reactivate.mutateAsync(user.user_id);
        }}
      />

      {/* The LEAVE (ADR-081 §4) — what ends, the chains handed over place by place or left as gaps the operator
          confirms; the sole-membership "remove & disable" rides the same dialog (`disable`). */}
      <LeaveDialog
        projectId={projectId}
        projectName={projectName}
        user={{ user_id: user.user_id, name }}
        open={removeOpen || orphanOpen}
        disable={orphanOpen}
        onClose={() => { setRemoveOpen(false); setOrphanOpen(false); }}
        onRemoved={() => { setRemoveOpen(false); setOrphanOpen(false); onRemoved?.(); }}
      />
    </>
  );

  return { acts, overlays };
}
